import type { CanvasStoreContext } from '../types';
import type { CanvasEntity } from '../entityTypes';

export type CommandHandler<P extends Record<string, unknown> = Record<string, unknown>> =
  (ctx: CanvasStoreContext, payload: P) => CanvasStoreContext;

export type CommandDefinitions = Record<string, CommandHandler<any>>;

/** Extracts the typed discriminated union from command definitions */
export type CommandsOf<D extends CommandDefinitions> = {
  [K in keyof D & string]: D[K] extends CommandHandler<infer P>
    ? { type: K } & P
    : never;
}[keyof D & string];

/** Define commands with full type inference. Handler signature = command payload type. */
export function defineCommands<const D extends CommandDefinitions>(defs: D): D {
  return defs;
}

/** Get all entities (combines all three maps). */
export function getAllEntities(ctx: CanvasStoreContext): CanvasEntity[] {
  return [
    ...Object.values(ctx.nodes),
    ...Object.values(ctx.connectors),
    ...Object.values(ctx.groups),
  ];
}

/** Find which map an entity lives in. Returns null if not found. */
export function getMapForEntity(ctx: CanvasStoreContext, entityId: string):
  { map: 'nodes' | 'connectors' | 'groups'; entity: CanvasEntity } | null {
  if (ctx.nodes[entityId]) return { map: 'nodes', entity: ctx.nodes[entityId] };
  if (ctx.connectors[entityId]) return { map: 'connectors', entity: ctx.connectors[entityId] };
  if (ctx.groups[entityId]) return { map: 'groups', entity: ctx.groups[entityId] };
  return null;
}

/** Returns the correct map key for a given entity kind. */
export function getMapKeyForKind(kind: string): 'connectors' | 'groups' | 'nodes' {
  if (kind === 'connector') return 'connectors';
  if (kind === 'group') return 'groups';
  return 'nodes';
}

/** Return version bumps for the given map keys. Spread into return object. */
export function bumpVersions(
  ctx: CanvasStoreContext,
  ...maps: ('nodes' | 'connectors' | 'groups')[]
): Pick<CanvasStoreContext, 'nodesVersion' | 'connectorsVersion' | 'groupsVersion'> {
  return {
    nodesVersion: maps.includes('nodes') ? ctx.nodesVersion + 1 : ctx.nodesVersion,
    connectorsVersion: maps.includes('connectors') ? ctx.connectorsVersion + 1 : ctx.connectorsVersion,
    groupsVersion: maps.includes('groups') ? ctx.groupsVersion + 1 : ctx.groupsVersion,
  };
}

/** Shared reducer for `<kind>.updateData` commands — merges dataChanges into entity.data. */
export function applyEntityDataChanges<T extends object>(
  ctx: CanvasStoreContext, entityId: string, dataChanges: T,
): CanvasStoreContext {
  const found = getMapForEntity(ctx, entityId);
  if (!found) return ctx;
  const updated = { ...found.entity, data: { ...found.entity.data, ...dataChanges } };
  return { ...ctx, [found.map]: { ...ctx[found.map], [entityId]: updated } };
}
